import React, { useEffect, useMemo, useState } from 'react';
import { Btn, Loading, Msg, Section, Select, SubTabs, panelGet, useAdminGet } from '../kit';
import { AdminLink } from '../adminNav';
import { Drawer } from '../email/emailKit';
import {
  Collapse, DiffView, FieldChips, Layer, Pill, type RecrawlCtx, RunLink, S, type Tone, arr, bucketLabel, eventColor, fmtIST,
  fmtN, n0, num, obj, str, useRecrawlLayer,
} from './shared';

/**
 * Recrawl → Changes → Change heatmap (owner, 2026-10-26: "something like that heatmap … for the univ
 * data additions/removals etc … when we click on it we see univ wise details for removals/additions/
 * modifications").
 *
 * GET heatmap {dimension, metric, granularity, from, to, source, code?} (Backend/recrawl/insights.py):
 * months or weeks down the side (newest first), universities / sectors / change types across, a Total
 * column and a Total row, cells shaded by value (the look of Content → Sectors & keywords → Pivot).
 * A cell, a period, a column header or a total opens the drawer (GET heatmap_cell, paged; GET
 * heatmap_export for the CSV) listing the technologies with their change, date, changed fields, a short
 * before → after, and links to the live page, the source page and the full history.
 *
 * UniversityChangeHeatmap is the compact month × change-type grid of one university for its drawer.
 * The chosen view (columns, count, period size, origin, columns shown, swap) is remembered in this
 * browser.
 */

export type HeatDim = 'university' | 'sector' | 'event_type';
export type HeatMetric = 'all' | 'added' | 'modified' | 'removed' | 'readded' | 'moved' | 'missing' | 'net';
type Gran = 'month' | 'week';
type Src = 'all' | 'engine' | 'backfill' | 'other';

const DIMS: HeatDim[] = ['university', 'sector', 'event_type'];
const DIM_TAB: Record<HeatDim, string> = { university: 'By university', sector: 'By sector', event_type: 'By change type' };
const DIM_NOUN: Record<HeatDim, [string, string]> = {
  university: ['university', 'universities'], sector: ['sector', 'sectors'], event_type: ['change type', 'change types'],
};
const METRICS: HeatMetric[] = ['all', 'added', 'modified', 'removed', 'readded', 'moved', 'missing', 'net'];
export const METRIC_LABEL: Record<HeatMetric, string> = {
  all: 'All changes', added: 'Added', modified: 'Modified', removed: 'Removed', readded: 'Re-added',
  moved: 'Moved (new URL)', missing: 'Missing (not yet removed)', net: 'Net listed change',
};
const METRIC_HINT: Record<HeatMetric, string> = {
  all: 'added + modified + removed + re-added + moved',
  added: 'new technologies', modified: 'content changed', removed: 'no longer listed', readded: 'came back after a removal',
  moved: 'same technology under a new URL', missing: 'absent from a run, not removed yet',
  net: 'added + re-added − removed: green is growth, red is shrinkage',
};
const SOURCES: Src[] = ['all', 'engine', 'backfill', 'other'];
const SOURCE_LABEL: Record<Src, string> = {
  all: 'All origins', engine: 'Recrawl runs', backfill: 'Backfilled history', other: 'Manual loads',
};
const TYPE_LABEL: Record<string, string> = {
  added: 'Added', modified: 'Modified', removed: 'Removed', readded: 'Re-added', moved: 'Moved', missing: 'Missing',
  manual_fix: 'Manual fix',
};
const TYPE_TONE: Record<string, Tone> = {
  added: 'green', modified: 'blue', removed: 'red', readded: 'purple', moved: 'navy', missing: 'amber', manual_fix: 'grey',
};
const TOPS = ['10', '20', '40', 'all'];
const OTHER = '__other__';
const PAGE_SIZE = 50;
const LS_KEY = 'utech_admin_recrawl:heatmap';

/** Today as an India calendar day ('YYYY-MM-DD'); India has no daylight saving. */
const todayIST = () => new Date(Date.now() + 330 * 60000).toISOString().slice(0, 10);

// ---------------------------------------------------------------------------
// shapes
// ---------------------------------------------------------------------------
type Counts = Record<string, number>;
type HRow = { key: string; end: string; total: number; events: number; bd: Counts; partial: boolean };
type HCol = { key: string; label: string; short: string; total: number; events: number; bd: Counts };
type HeatData = {
  dimension: HeatDim; metric: HeatMetric; granularity: Gran; types: string[]; rows: HRow[]; cols: HCol[];
  cells: Record<string, any>; breakdown: Record<string, any>; typeTotals: Counts; grand: number; events: number;
  note: string; generatedAt: string; tookMs: number;
};
type Cell = { v: number; events: number; bd: Counts };
/** One row or column of the grid as drawn: a period or a key (university / sector / type), or "Other". */
type Item = {
  key: string; label: string; short: string; title: string; total: number; events: number; bd: Counts;
  isPeriod: boolean; partial?: boolean; other?: boolean;
};
/** What the drawer lists: heatmap_cell's parameters and how to name them. */
export type CellSpec = { title: string; params: Record<string, string>; context: string };

const counts = (v: any): Counts => {
  const out: Counts = {};
  Object.entries(obj(v)).forEach(([k, x]) => { out[k] = n0(x); });
  return out;
};
const sumCounts = (c: Counts) => Object.values(c).reduce((a, b) => a + b, 0);

const toHeat = (raw: any): HeatData | null => {
  const d = obj(raw);
  if (!Array.isArray(d.rows)) return null;
  return {
    dimension: (DIMS.includes(str(d.dimension) as HeatDim) ? str(d.dimension) : 'university') as HeatDim,
    metric: (METRICS.includes(str(d.metric) as HeatMetric) ? str(d.metric) : 'all') as HeatMetric,
    granularity: str(d.granularity) === 'week' ? 'week' : 'month',
    types: arr(d.types).map(str),
    rows: arr(d.rows).map(r => ({ key: str(r.key), end: str(r.end), total: n0(r.total), events: n0(r.events),
      bd: counts(r.breakdown), partial: !!r.partial })),
    cols: arr(d.cols).map(c => ({ key: str(c.key), label: str(c.label || c.key), short: str(c.short || c.key),
      total: n0(c.total), events: n0(c.events), bd: counts(c.breakdown) })),
    cells: obj(d.cells), breakdown: obj(d.breakdown), typeTotals: counts(d.type_totals),
    grand: n0(d.grand_total), events: n0(d.events), note: str(d.note), generatedAt: str(d.generated_at),
    tookMs: n0(d.took_ms),
  };
};

const fmtSigned = (v: number) => (v > 0 ? `+${v.toLocaleString()}` : v < 0 ? `−${Math.abs(v).toLocaleString()}` : '0');
const bdText = (bd: Counts, types: string[]) =>
  types.filter(t => bd[t]).map(t => `${(TYPE_LABEL[t] || t).toLowerCase()} ${fmtN(bd[t])}`).join(' · ');
const periodName = (pk: string, gran: Gran) => bucketLabel(pk, gran, true);
const dayText = (iso: string) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return iso;
  return new Date(Date.UTC(+m[1], +m[2] - 1, +m[3])).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
};

const downloadText = (name: string, text: string) => {
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([text], { type: 'text/csv;charset=utf-8' }));
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 2000);
};
const csvLine = (cells: (string | number)[]) => cells.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',');

// ---------------------------------------------------------------------------
// remembered view
// ---------------------------------------------------------------------------
type Cfg = { dim: HeatDim; metric: HeatMetric; gran: 'auto' | Gran; source: Src; top: Record<HeatDim, string>; swap: boolean };
const DEFAULT_CFG: Cfg = {
  dim: 'university', metric: 'all', gran: 'auto', source: 'all',
  top: { university: 'all', sector: '20', event_type: 'all' }, swap: false,
};
const loadCfg = (): Cfg => {
  try {
    const v = obj(JSON.parse(localStorage.getItem(LS_KEY) || 'null'));
    const top = obj(v.top);
    const pickTop = (k: HeatDim) => (TOPS.includes(str(top[k])) ? str(top[k]) : DEFAULT_CFG.top[k]);
    return {
      dim: DIMS.includes(v.dim) ? v.dim : DEFAULT_CFG.dim,
      metric: METRICS.includes(v.metric) ? v.metric : DEFAULT_CFG.metric,
      gran: ['auto', 'month', 'week'].includes(v.gran) ? v.gran : 'auto',
      source: SOURCES.includes(v.source) ? v.source : 'all',
      top: { university: pickTop('university'), sector: pickTop('sector'), event_type: pickTop('event_type') },
      swap: !!v.swap,
    };
  } catch { return DEFAULT_CFG; }
};
const saveCfg = (c: Cfg) => {
  try { localStorage.setItem(LS_KEY, JSON.stringify(c)); } catch { /* private window: not remembered */ }
};

// ---------------------------------------------------------------------------
// the grid
// ---------------------------------------------------------------------------
/** Rows × columns of the heat table; periods and keys can be on either axis. */
const HeatGrid: React.FC<{
  rows: Item[]; cols: Item[]; cell: (r: Item, c: Item) => Cell; net: boolean; types: string[];
  corner: string; narrowCols: boolean; grand: number; grandEvents: number; compact?: boolean; maxH?: string;
  /** True while a new heatmap loads: the old grid stays on screen but cannot be clicked (its cells belong to the old view). */
  locked?: boolean;
  onCell: (r: Item, c: Item) => void; onRow: (r: Item) => void; onCol: (c: Item) => void; onAll: () => void;
}> = ({ rows, cols, cell, net, types, corner, narrowCols, grand, grandEvents, compact, maxH, locked, onCell, onRow, onCol, onAll }) => {
  const grid = useMemo(() => rows.map(r => cols.map(c => cell(r, c))), [rows, cols, cell]);
  const maxAbs = useMemo(() => Math.max(0, ...grid.flatMap((line, i) => line
    .filter((_x, j) => !rows[i].other && !cols[j].other).map(x => Math.abs(x.v)))), [grid, rows, cols]);
  const fmt = (v: number) => (net ? fmtSigned(v) : fmtN(v));
  // sqrt: one huge month (a backfill load) would otherwise wash every other cell out to white.
  const shade = (x: Cell, other: boolean): React.CSSProperties => {
    if (!x.events) return {};
    if (other) return { background: '#f5f5f5', color: '#141414' };
    const a = maxAbs ? 0.06 + 0.84 * Math.sqrt(Math.min(1, Math.abs(x.v) / maxAbs)) : 0.06;
    if (net) {
      if (x.v === 0) return { background: '#eeeeee', color: '#141414' };
      const rgb = x.v > 0 ? '47, 158, 91' : '208, 68, 58';
      return { background: `rgba(${rgb}, ${a.toFixed(3)})`, color: a > 0.55 ? '#ffffff' : '#141414' };
    }
    return { background: `rgba(128, 101, 63, ${a.toFixed(3)})`, color: a > 0.5 ? '#ffffff' : '#141414' };
  };
  const tip = (r: Item, c: Item, x: Cell) => {
    const head = `${r.title} × ${c.title}: ${fmt(x.v)}${net ? ' net' : ''}`;
    const parts = bdText(x.bd, types);
    return `${head}${parts ? `\n${parts}` : ''}${r.other || c.other ? '\nsummed over the columns not shown' : x.events && !locked ? '\nClick to list them' : ''}`;
  };
  const LABEL_W = compact ? 'w-28 min-w-[7rem] max-w-[7rem]'
    : 'w-32 min-w-[8rem] max-w-[8rem] sm:w-56 sm:min-w-[14rem] sm:max-w-[14rem]';
  const TOTAL_W = compact ? 'w-[4.5rem] min-w-[4.5rem] max-w-[4.5rem] left-28'
    : 'w-[4.5rem] min-w-[4.5rem] max-w-[4.5rem] left-32 sm:w-[5.5rem] sm:min-w-[5.5rem] sm:max-w-[5.5rem] sm:left-56';
  const COL_W = narrowCols ? 'w-16 min-w-[4rem] max-w-[4rem]' : 'w-24 min-w-[6rem] max-w-[6rem]';
  const headBtn = (it: Item, text: string, onClick: () => void, cls: string) => (it.other ? (
    <span className={`${cls} italic text-grayscale-1100`} title="The columns not shown, added together">{text}</span>
  ) : (
    <button type="button" onClick={onClick} disabled={locked} title={`${it.title}: ${fmt(it.total)}${bdText(it.bd, types) ? `\n${bdText(it.bd, types)}` : ''}${locked ? '' : '\nClick to list them'}`}
      className={`${cls} ${locked ? 'cursor-default' : 'hover:text-primary-700'}`}>
      {text}{it.partial && <span className="text-grayscale-1100 font-normal" title="Only part of this period is inside the chosen dates">*</span>}
    </button>
  ));

  return (
    <div className={`overflow-auto ${maxH || 'max-h-[70vh]'} max-w-full rounded-lg border`}>
      <table className="text-xs border-separate" style={{ borderSpacing: 0 }}>
        <thead>
          <tr>
            <th className={`sticky top-0 left-0 z-30 bg-grayscale-200 ${LABEL_W} px-2 py-2 text-left align-bottom font-grotesk font-medium text-grayscale-1100`}>
              {corner}
            </th>
            <th className={`sticky top-0 z-30 bg-grayscale-200 ${TOTAL_W} px-2 py-2 text-right align-bottom font-grotesk font-bold text-grayscale-1300 border-r`}>
              Total
            </th>
            {cols.map(c => (
              <th key={c.key} className={`sticky top-0 z-20 bg-grayscale-200 ${COL_W} px-1 py-2 align-bottom font-grotesk font-medium text-grayscale-1200`}>
                {headBtn(c, c.short, () => onCol(c), 'block w-full text-center leading-tight line-clamp-3 [overflow-wrap:anywhere]')}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.key}>
              <th className={`sticky left-0 z-10 bg-white ${LABEL_W} px-2 py-0.5 text-left font-normal border-t border-grayscale-200`}>
                {headBtn(r, r.label, () => onRow(r), 'block w-full truncate text-left text-grayscale-1300')}
              </th>
              <td className={`sticky z-10 bg-grayscale-200 ${TOTAL_W} px-2 py-0.5 text-right tabular-nums font-bold text-grayscale-1300 border-t border-r border-grayscale-200`}>
                {r.other ? <span className="italic font-normal">{fmt(r.total)}</span> : (
                  <button type="button" onClick={() => onRow(r)} disabled={!r.events || locked} className="hover:text-primary-700 disabled:text-grayscale-600"
                    title={`${r.title}: ${fmt(r.total)}${bdText(r.bd, types) ? `\n${bdText(r.bd, types)}` : ''}${r.events ? '\nClick to list them' : ''}`}>
                    {r.events ? fmt(r.total) : '·'}
                  </button>
                )}
              </td>
              {cols.map((c, j) => {
                const x = grid[i][j];
                const other = !!(r.other || c.other);
                return (
                  <td key={c.key} className="p-[1px] bg-white border-t border-grayscale-200">
                    <button type="button" disabled={!x.events || other || locked} onClick={() => onCell(r, c)} title={tip(r, c, x)}
                      className={`w-full ${compact ? 'h-6' : 'h-7'} rounded tabular-nums text-[11px] ${x.events && !other && !locked ? 'hover:ring-2 hover:ring-primary-500' : 'cursor-default'}
                        ${x.events ? '' : 'text-grayscale-600'} ${other ? 'italic' : ''}`}
                      style={shade(x, other)}>
                      {x.events ? fmt(x.v) : '·'}
                    </button>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <th className={`sticky bottom-0 left-0 z-30 bg-grayscale-200 ${LABEL_W} px-2 py-1.5 text-left font-grotesk font-bold text-grayscale-1300`}>
              Total
            </th>
            <td className={`sticky bottom-0 z-30 bg-grayscale-200 ${TOTAL_W} px-2 py-1.5 text-right tabular-nums font-bold text-grayscale-1300 border-r`}>
              <button type="button" onClick={onAll} disabled={!grandEvents || locked} className="hover:text-primary-700"
                title={`Every change shown: ${fmt(grand)} — click to list them`}>
                {fmt(grand)}
              </button>
            </td>
            {cols.map(c => (
              <td key={c.key} className="sticky bottom-0 z-20 bg-grayscale-200 px-1 py-1.5 text-center tabular-nums font-bold text-grayscale-1200">
                {c.other ? <span className="italic font-normal">{fmt(c.total)}</span> : (
                  <button type="button" onClick={() => onCol(c)} disabled={!c.events || locked} className="hover:text-primary-700"
                    title={`${c.title}: ${fmt(c.total)} — click to list them`}>
                    {c.events ? fmt(c.total) : '·'}
                  </button>
                )}
              </td>
            ))}
          </tr>
        </tfoot>
      </table>
    </div>
  );
};

/** Items and a cell lookup for a heatmap response; keys past `top` fold into one "Other" column. */
function useGridModel(d: HeatData | null, top: number, uniName: (code: string) => string) {
  return useMemo(() => {
    if (!d) return null;
    const gran = d.granularity;
    const periods: Item[] = d.rows.map(r => ({
      key: r.key, label: periodName(r.key, gran), short: bucketLabel(r.key, gran, false),
      title: `${periodName(r.key, gran)}${r.partial ? ' (part of it is outside the dates)' : ''}`,
      total: r.total, events: r.events, bd: r.bd, isPeriod: true, partial: r.partial,
    }));
    const keyItem = (c: HCol): Item => {
      const title = d.dimension === 'university' ? (c.key === '__none__' ? c.label : `${c.short} — ${uniName(c.short) || c.label}`) : c.label;
      return { key: c.key, label: title, short: c.short, title, total: c.total, events: c.events, bd: c.bd, isPeriod: false };
    };
    const shown = d.cols.slice(0, top);
    const folded = d.cols.slice(top);
    const keys: Item[] = shown.map(keyItem);
    if (folded.length) {
      const bd: Counts = {};
      folded.forEach(c => Object.entries(c.bd).forEach(([t, n]) => { bd[t] = (bd[t] || 0) + n; }));
      const label = `Other (${folded.length})`;
      keys.push({ key: OTHER, label, short: label, title: `${folded.length} more ${DIM_NOUN[d.dimension][1]}`,
        total: folded.reduce((a, c) => a + c.total, 0), events: folded.reduce((a, c) => a + c.events, 0), bd, isPeriod: false, other: true });
    }
    const one = (pk: string, kk: string): Cell => {
      const bd = counts(obj(obj(d.breakdown[pk])[kk]));
      return { v: n0(obj(d.cells[pk])[kk]), events: sumCounts(bd), bd };
    };
    const lookup = (pk: string, kk: string): Cell => {
      if (kk !== OTHER) return one(pk, kk);
      const acc: Cell = { v: 0, events: 0, bd: {} };
      folded.forEach(c => {
        const x = one(pk, c.key);
        acc.v += x.v; acc.events += x.events;
        Object.entries(x.bd).forEach(([t, n]) => { acc.bd[t] = (acc.bd[t] || 0) + n; });
      });
      return acc;
    };
    return { periods, keys, lookup, folded: folded.length };
  }, [d, top, uniName]);
}

// ---------------------------------------------------------------------------
// the drawer: technologies behind a cell / row / column
// ---------------------------------------------------------------------------
const Origin: React.FC<{ ctx: RecrawlCtx; row: any }> = ({ ctx, row }) => {
  const k = str(row.source_kind);
  if (k === 'engine') {
    return <span className="inline-flex items-center gap-1">Recrawl run {num(row.run_id) !== null && <RunLink ctx={ctx} id={row.run_id} />}</span>;
  }
  return <span>{k === 'backfill' ? 'Backfilled history' : 'Manual load'}</span>;
};

const ChangeRow: React.FC<{ ctx: RecrawlCtx; r: any; onHistory: (key: string) => void; inUniversity?: string }> = ({ ctx, r, onHistory, inUniversity }) => {
  const t = str(r.event_type);
  const id = str(r.utech_technology_id);
  const live = r.in_live;
  const diffN = Object.keys(obj(r.diff)).length;
  const pageId = str(r.live_id) || id;
  // A plain addition / removal says nothing the pill does not; show the line when it adds something.
  const summary = str(r.summary);
  const plain = !arr(r.changed_fields).length && !Object.keys(obj(r.diff)).length && t !== 'moved';
  // Backfilled events carry the loader's snapshot bookkeeping as their note: "Backfilled history" says it.
  const note = str(r.source_kind) === 'backfill' ? '' : str(r.note);
  return (
    <div className="flex flex-col gap-1 py-2 border-b border-grayscale-200 last:border-b-0">
      <div className="flex items-start gap-2">
        <Pill tone={TYPE_TONE[t] || 'grey'}>{TYPE_LABEL[t] || t}</Pill>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-bold text-grayscale-1300 [overflow-wrap:anywhere]">{str(r.title) || '(no title)'}</div>
          <div className="text-[11px] text-grayscale-1100 flex flex-wrap gap-x-2">
            {inUniversity && str(r.university_code).toUpperCase() === inUniversity.toUpperCase() ? (
              // Already inside this university's drawer: opening it again would do nothing.
              <span className="font-medium">{str(r.university_code)}</span>
            ) : (
              <button type="button" className="text-primary-700 hover:underline font-medium" onClick={() => ctx.openUniversity(str(r.university_code))}
                title={`${str(r.university_name)} — open`}>{str(r.university_code) || '—'}</button>
            )}
            <span className="truncate max-w-[16rem]">{str(r.university_name)}</span>
            {id && <code className="font-mono">{id}</code>}
            {arr(r.sectors).length > 0 && <span className="truncate max-w-[20rem]" title={arr(r.sectors).join('; ')}>{arr(r.sectors).join(' · ')}</span>}
          </div>
        </div>
        <span className="text-[11px] text-grayscale-1200 whitespace-nowrap text-right">{fmtIST(r.occurred_at)}</span>
      </div>
      {summary && !plain && <div className="text-xs text-grayscale-1200 [overflow-wrap:anywhere] pl-1">{summary}</div>}
      <div className="flex items-center gap-3 flex-wrap text-xs pl-1">
        <FieldChips fields={r.changed_fields} max={5} quiet />
        <span className="flex-1" />
        <span className="text-[11px] text-grayscale-1100"><Origin ctx={ctx} row={r} /></span>
        {str(r.live_page_url) ? (
          <a href={str(r.live_page_url)} target="_blank" rel="noreferrer noopener" className="text-primary-700 hover:underline">Live page ↗</a>
        ) : pageId && live !== false && str(r.master_status) !== 'removed' ? (
          <AdminLink to={{ group: 'tech_pages', tab: 'tech', target: pageId }} title="Its page on the site (Tech pages)">Tech page</AdminLink>
        ) : (
          <span className="text-[11px] text-grayscale-1100" title={live === false ? 'Not in the live catalogue' : 'Removed from the master'}>not live</span>
        )}
        {str(r.url) && <a href={str(r.url)} target="_blank" rel="noreferrer noopener" className="text-primary-700 hover:underline" title={str(r.url)}>Source ↗</a>}
        <button type="button" className="text-primary-700 hover:underline" onClick={() => onHistory(id || str(r.url))}
          title="Every version and change of this technology">History</button>
      </div>
      {(diffN > 0 || note) && (
        <div className="pl-1">
          <Collapse title={diffN ? `Before / after (${diffN} field${diffN === 1 ? '' : 's'})` : 'Details'}>
            {note && <div className="text-xs text-grayscale-1200 mb-1">Note: {note}</div>}
            {diffN > 0 && <DiffView diff={r.diff} fields={r.changed_fields} />}
          </Collapse>
        </div>
      )}
    </div>
  );
};

export const HeatmapDrawer: React.FC<{
  spec: CellSpec; ctx: RecrawlCtx; onClose: () => void; onHistory?: (key: string) => void;
  /** Set when the drawer opens inside that university's own drawer. */
  inUniversity?: string;
}> = ({ spec, ctx, onClose, onHistory, inUniversity }) => {
  const pkey = JSON.stringify(spec.params);
  const [type, setType] = useState('');
  const [page, setPage] = useState(1);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  useEffect(() => { setType(''); setPage(1); setNote(null); }, [pkey]);
  const params = useMemo(() => {
    const p: Record<string, string | number> = { ...spec.params, page, page_size: PAGE_SIZE };
    if (type) p.type = type;
    return p;
  }, [spec.params, type, page]);
  const res = useAdminGet<any>(S, 'heatmap_cell', params, [pkey, type, page]);
  const d = obj(res.data);
  const sc = obj(d.scope);
  const types = arr(sc.types).map(str);
  const cnt = counts(d.counts);
  const all = sumCounts(cnt);
  const total = n0(d.total);
  const pages = Math.max(1, n0(d.pages));
  const rows = arr(d.rows);

  // Escape: this drawer is a layer; RecrawlPanel closes whichever drawer is on top (a university or a
  // run opened from a row here sits above it).
  const layer = useRecrawlLayer(onClose);

  const history = (k: string) => {
    if (!k) return;
    onClose();
    if (onHistory) onHistory(k); else ctx.goChanges({ tech: k });
  };
  const exportCsv = async () => {
    setBusy(true); setNote(null);
    const p: Record<string, string> = { ...spec.params };
    if (type) p.type = type;
    const { data, error } = await panelGet(S, 'heatmap_export', p);
    setBusy(false);
    if (error) { setNote({ kind: 'error', text: error }); return; }
    const o = obj(data);
    downloadText(str(o.filename) || 'recrawl_changes.csv', str(o.csv));
    if (o.truncated) setNote({ kind: 'ok', text: `The file holds the newest ${fmtN(o.rows)} of ${fmtN(o.total)} changes.` });
  };
  const metric = (str(sc.metric) || spec.params.metric || 'all') as HeatMetric;

  return (
    <Layer id={layer}>
    <Drawer title={spec.title} onClose={onClose}
      subtitle={res.data ? `${fmtN(all)} change${all === 1 ? '' : 's'} · ${METRIC_LABEL[metric] || metric} · ${spec.context}` : 'Loading…'}>
      <div className="flex items-center gap-1.5 flex-wrap">
        <button type="button" onClick={() => { setType(''); setPage(1); }}
          className={`py-1 px-2.5 rounded-full text-xs font-grotesk ${!type ? 'bg-primary-500 text-grayscale-100 font-bold' : 'border border-grayscale-400 text-grayscale-1200 hover:bg-grayscale-200'}`}>
          All {fmtN(all)}
        </button>
        {types.map(t => (
          <button key={t} type="button" disabled={!cnt[t]} onClick={() => { setType(t); setPage(1); }}
            className={`inline-flex items-center gap-1.5 py-1 px-2.5 rounded-full text-xs font-grotesk disabled:opacity-40 ${type === t ? 'bg-primary-500 text-grayscale-100 font-bold' : 'border border-grayscale-400 text-grayscale-1200 hover:bg-grayscale-200'}`}>
            <span className="inline-block w-2 h-2 rounded-full" style={{ background: eventColor(t) }} />
            {TYPE_LABEL[t] || t} {fmtN(cnt[t] || 0)}
          </button>
        ))}
        <span className="flex-1" />
        <Btn small onClick={exportCsv} disabled={busy || !total} title="These changes as a spreadsheet (newest first)">
          {busy ? 'Preparing…' : 'Download CSV'}
        </Btn>
      </div>
      {metric === 'net' && res.data && (
        <div className="text-xs text-grayscale-1100">Net {fmtSigned(n0(d.value))}: added and re-added count up, removed counts down.</div>
      )}
      {note && <Msg kind={note.kind}>{note.text}</Msg>}
      {res.error && <Msg kind="error">{res.error}</Msg>}
      {res.loading && !res.data ? <Loading label="Listing the changes…" /> : (
        <div className={`flex flex-col ${res.loading ? 'opacity-60' : ''}`}>
          {!rows.length && <div className="text-sm text-grayscale-1100 py-6 text-center">No changes here.</div>}
          {rows.map(r => <ChangeRow key={str(r.id)} ctx={ctx} r={r} onHistory={history} inUniversity={inUniversity} />)}
        </div>
      )}
      {pages > 1 && (
        <div className="flex items-center gap-2 justify-center text-xs">
          <Btn small disabled={page <= 1 || res.loading} onClick={() => setPage(p => Math.max(1, p - 1))}>‹ Newer</Btn>
          <span className="text-grayscale-1200">Page {fmtN(page)} of {fmtN(pages)} · {fmtN(total)} change{total === 1 ? '' : 's'}</span>
          <Btn small disabled={page >= pages || res.loading} onClick={() => setPage(p => Math.min(pages, p + 1))}>Older ›</Btn>
        </div>
      )}
    </Drawer>
    </Layer>
  );
};

// ---------------------------------------------------------------------------
// Changes → Change heatmap
// ---------------------------------------------------------------------------
const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="flex flex-col gap-1 text-xs text-grayscale-1100 min-w-0">
    <span>{label}</span>
    {children}
  </div>
);

export const ChangeHeatmap: React.FC<{
  ctx: RecrawlCtx; range: { from: string; to: string }; rangeText: string; code?: string;
  onHistory?: (key: string) => void;
}> = ({ ctx, range, rangeText, code, onHistory }) => {
  const [cfg, setCfgRaw] = useState<Cfg>(loadCfg);
  const setCfg = (patch: Partial<Cfg>) => setCfgRaw(c => { const n = { ...c, ...patch }; saveCfg(n); return n; });
  const spanDays = Math.max(1, Math.round((Date.parse(range.to) - Date.parse(range.from)) / 86400000) + 1);
  const gran: Gran = cfg.gran !== 'auto' ? cfg.gran : spanDays <= 120 ? 'week' : 'month';
  // One university selected: its columns would be a single one, so it is shown by change type.
  const dim: HeatDim = code && cfg.dim === 'university' ? 'event_type' : cfg.dim;
  const base = useMemo(() => {
    const p: Record<string, string> = { dimension: dim, metric: cfg.metric, granularity: gran, from: range.from, to: range.to, source: cfg.source };
    if (code) p.code = code;
    return p;
  }, [dim, cfg.metric, gran, range.from, range.to, cfg.source, code]);
  const res = useAdminGet<any>(S, 'heatmap', base, [JSON.stringify(base), ctx.tick]);
  const d = useMemo(() => toHeat(res.data), [res.data]);
  const top = cfg.top[dim] === 'all' ? Number.MAX_SAFE_INTEGER : Number(cfg.top[dim]) || 20;
  const uniName = useMemo(() => (c: string) => ctx.uniByCode.get(c)?.name || '', [ctx.uniByCode]);
  const model = useGridModel(d, top, uniName);
  const [spec, setSpec] = useState<CellSpec | null>(null);

  const net = d?.metric === 'net';
  const swap = cfg.swap;
  const rowsAxis = model ? (swap ? model.keys : model.periods) : [];
  const colsAxis = model ? (swap ? model.periods : model.keys) : [];
  const cellFn = useMemo(() => (r: Item, c: Item): Cell => {
    if (!model) return { v: 0, events: 0, bd: {} };
    return r.isPeriod ? model.lookup(r.key, c.key) : model.lookup(c.key, r.key);
  }, [model]);

  const where = code ? (ctx.uniByCode.get(code)?.name ? `${ctx.uniByCode.get(code)?.name} (${code})` : code) : 'all universities';
  // A drill-down describes the grid on screen: the parameters come from the loaded response, not from
  // the controls (which may already ask for the next view), and the grid is locked while that loads.
  const loaded = useMemo(() => {
    const r = obj(res.data);
    const p: Record<string, string> = {
      dimension: str(r.dimension) || base.dimension, metric: str(r.metric) || base.metric,
      granularity: str(r.granularity) || base.granularity, from: range.from, to: range.to,
      source: str(r.source) || base.source,
    };
    if (str(r.code)) p.code = str(r.code);
    return p;
  }, [res.data, base, range.from, range.to]);
  const locked = res.loading;
  const loadedSource = (SOURCES.includes(loaded.source as Src) ? loaded.source : 'all') as Src;
  const context = `${rangeText} · ${SOURCE_LABEL[loadedSource]}${loaded.code ? ` · ${loaded.code}` : ''}`;
  const keyTitle = (it: Item) => it.title;
  const open = (title: string, extra: Record<string, string>) => {
    if (locked) return;
    setSpec({ title, params: { ...loaded, ...extra }, context });
  };
  const onCell = (r: Item, c: Item) => {
    const p = r.isPeriod ? r : c;
    const k = r.isPeriod ? c : r;
    if (k.other || !d) return;
    open(`${keyTitle(k)} · ${periodName(p.key, d.granularity)}`, { key: k.key, period: p.key });
  };
  const onPeriod = (p: Item) => d && open(`${periodName(p.key, d.granularity)} — ${code ? where : `all ${DIM_NOUN[d.dimension][1]}`}`, { period: p.key });
  const onKey = (k: Item) => { if (!k.other) open(keyTitle(k), { key: k.key }); };
  const onRow = (r: Item) => (r.isPeriod ? onPeriod(r) : onKey(r));
  const onCol = (c: Item) => (c.isPeriod ? onPeriod(c) : onKey(c));
  const onAll = () => d && open(`${METRIC_LABEL[d.metric]} — ${where}`, {});

  const exportGrid = () => {
    if (!d || !model) return;
    const rl = swap ? DIM_NOUN[dim][0] : (gran === 'week' ? 'Week' : 'Month');
    const lines = [csvLine([`${rl} (${METRIC_LABEL[d.metric]})`, 'Total', ...colsAxis.map(c => c.label)])];
    rowsAxis.forEach(r => lines.push(csvLine([r.label, r.total, ...colsAxis.map(c => cellFn(r, c).v)])));
    lines.push(csvLine(['Total', d.grand, ...colsAxis.map(c => c.total)]));
    downloadText(`recrawl_heatmap_${d.metric}_${dim}_${gran}_${range.from}_${range.to}.csv`, lines.join('\n'));
  };

  const dims = code ? DIMS.filter(x => x !== 'university') : DIMS;
  const typesShown = d ? d.types.filter(t => d.typeTotals[t]) : [];
  const periodsWithChanges = d ? d.rows.filter(r => r.events).length : 0;
  const cornerRows = swap ? DIM_NOUN[dim][1] : (gran === 'week' ? 'Week' : 'Month');
  const cornerCols = swap ? (gran === 'week' ? 'weeks' : 'months') : DIM_NOUN[dim][1];

  return (
    <Section title={`Change heatmap — ${where}`}
      right={<Btn small onClick={exportGrid} disabled={!d || !d.events} title="The table as shown (rows, columns, totals)">Download CSV</Btn>}>
      <div className="text-xs text-grayscale-1100 -mt-1">
        Each cell counts the changes in one {gran} for one {DIM_NOUN[dim][0]}; darker means more. Click a cell, a {gran}, a column
        or a total to see the technologies behind it.
      </div>
      <div className="flex gap-3 items-end flex-wrap">
        <Field label="Columns">
          <SubTabs tabs={dims.map(x => DIM_TAB[x])} active={DIM_TAB[dim]}
            onChange={t => setCfg({ dim: (DIMS.find(x => DIM_TAB[x] === t) || 'university') })} />
        </Field>
        <Field label="Count">
          <Select value={cfg.metric} onChange={v => setCfg({ metric: (METRICS.includes(v as HeatMetric) ? v : 'all') as HeatMetric })}
            options={METRICS} labels={METRIC_LABEL} />
        </Field>
        <Field label="Per">
          <Select value={cfg.gran} onChange={v => setCfg({ gran: (v === 'month' || v === 'week' ? v : 'auto') })}
            options={['auto', 'month', 'week']} labels={{ auto: `Auto (${gran})`, month: 'Month', week: 'Week' }} />
        </Field>
        <Field label="Origin">
          <Select value={cfg.source} onChange={v => setCfg({ source: (SOURCES.includes(v as Src) ? v : 'all') as Src })}
            options={SOURCES} labels={SOURCE_LABEL} />
        </Field>
        {dim !== 'event_type' && (
          <Field label="Columns shown">
            <Select value={cfg.top[dim]} onChange={v => setCfg({ top: { ...cfg.top, [dim]: v } })} options={TOPS}
              labels={{ 10: 'Top 10', 20: 'Top 20', 40: 'Top 40', all: 'All' }} />
          </Field>
        )}
        <button type="button" onClick={() => setCfg({ swap: !cfg.swap })} title="Swap rows and columns"
          className="h-9 px-2 rounded-md border text-sm text-grayscale-1200 hover:bg-grayscale-200">⇄</button>
      </div>
      <div className="text-[11px] text-grayscale-1100">{METRIC_LABEL[cfg.metric]}: {METRIC_HINT[cfg.metric]}.</div>

      {res.error && <Msg kind="error">{res.error}</Msg>}
      {!d || !model ? (res.loading ? <Loading label="Building the heatmap…" /> : null) : (
        <div className={`flex flex-col gap-2 min-w-0 ${res.loading ? 'opacity-60' : ''}`}>
          {!d.events ? (
            <div className="text-sm text-grayscale-1100 py-6 text-center">No changes of this kind in these dates.</div>
          ) : (
            <HeatGrid rows={rowsAxis} cols={colsAxis} cell={cellFn} net={net} types={d.types}
              corner={`${cornerRows} ↓ · ${cornerCols} →`} narrowCols={swap || dim !== 'sector'}
              grand={d.grand} grandEvents={d.events} locked={locked} onCell={onCell} onRow={onRow} onCol={onCol} onAll={onAll} />
          )}
          <div className="text-xs text-grayscale-1300">
            <b>{net ? fmtSigned(d.grand) : fmtN(d.grand)}</b> {net ? 'net listed change' : `change${d.grand === 1 ? '' : 's'}`}
            {typesShown.length > 0 && <> ({typesShown.map(t => `${(TYPE_LABEL[t] || t).toLowerCase()} ${fmtN(d.typeTotals[t])}`).join(' · ')})</>}
            {' '}in {fmtN(periodsWithChanges)} of {fmtN(d.rows.length)} {gran}s, across {fmtN(d.cols.length)} {DIM_NOUN[dim][d.cols.length === 1 ? 0 : 1]}
            {model.folded > 0 && <>; the {fmtN(model.folded)} smallest are folded into “Other”</>}
            {' '}· {rangeText} · {SOURCE_LABEL[cfg.source].toLowerCase()}.
            {d.rows.some(r => r.partial) && <> A * marks a {gran} only partly inside the dates.</>}
          </div>
          {d.note && <div className="text-xs text-grayscale-1100">Note: {d.note}</div>}
          <div className="text-[11px] text-grayscale-1100">
            Months and weeks follow India time. Updated {fmtIST(d.generatedAt)} (kept for up to a minute).
          </div>
        </div>
      )}
      {spec && <HeatmapDrawer spec={spec} ctx={ctx} onClose={() => setSpec(null)} onHistory={onHistory} />}
    </Section>
  );
};

// ---------------------------------------------------------------------------
// University drawer → changes by month and type
// ---------------------------------------------------------------------------
export const UniversityChangeHeatmap: React.FC<{ code: string; ctx: RecrawlCtx }> = ({ code, ctx }) => {
  const to = todayIST();
  const from = useMemo(() => {
    const [y, m] = to.split('-').map(Number);
    return new Date(Date.UTC(y, m - 1 - 11, 1)).toISOString().slice(0, 10);
  }, [to]);
  const base = useMemo<Record<string, string>>(() => (
    { dimension: 'event_type', metric: 'all', granularity: 'month', from, to, source: 'all', code }), [from, to, code]);
  const res = useAdminGet<any>(S, 'heatmap', base, [JSON.stringify(base), ctx.tick]);
  const d = useMemo(() => toHeat(res.data), [res.data]);
  const uniName = useMemo(() => (c: string) => ctx.uniByCode.get(c)?.name || '', [ctx.uniByCode]);
  const model = useGridModel(d, Number.MAX_SAFE_INTEGER, uniName);
  const [spec, setSpec] = useState<CellSpec | null>(null);
  const context = `${dayText(from)} – ${dayText(to)}`;
  const cellFn = useMemo(() => (r: Item, c: Item): Cell => (model ? model.lookup(r.key, c.key) : { v: 0, events: 0, bd: {} }), [model]);
  const open = (title: string, extra: Record<string, string>) => {
    if (res.loading) return;
    setSpec({ title, params: { ...base, ...extra }, context });
  };

  return (
    <Section title="Changes by month and type"
      right={<Btn small onClick={() => ctx.goChanges({ code })}>Full heatmap →</Btn>}>
      {res.error && <Msg kind="error">{res.error}</Msg>}
      {!d || !model ? (res.loading ? <Loading /> : null) : !d.events ? (
        <div className="text-sm text-grayscale-1100">No changes for this university in the last 12 months.</div>
      ) : (
        <>
          <HeatGrid rows={model.periods} cols={model.keys} cell={cellFn} net={false} types={d.types} compact maxH="max-h-[24rem]"
            corner="Month ↓" narrowCols grand={d.grand} grandEvents={d.events} locked={res.loading}
            onCell={(r, c) => open(`${code} · ${c.title} · ${r.label}`, { key: c.key, period: r.key })}
            onRow={r => open(`${code} · ${r.label}`, { period: r.key })}
            onCol={c => open(`${code} · ${c.title}`, { key: c.key })}
            onAll={() => open(`${code} · all changes`, {})} />
          <div className="text-[11px] text-grayscale-1100">
            {fmtN(d.grand)} change{d.grand === 1 ? '' : 's'} in the last 12 months. Click a cell to see the technologies.
          </div>
        </>
      )}
      {spec && <HeatmapDrawer spec={spec} ctx={ctx} onClose={() => setSpec(null)} inUniversity={code} />}
    </Section>
  );
};
